/**
 * @scriptApi / @scriptFn / @scriptEvent — declarative markers for engine capability
 * modules (the "global services" surface of the scripting API, e.g. `engine.physics`).
 *
 * They are the scripting-API analog of `@component`/`@field`: pure markers that the
 * `script-api-codegen` reads STATICALLY (TS-AST) to build the language-neutral
 * `engine-api.ir.generated.json`, which is then projected into `EngineApi.generated.ts`
 * (TS consumers) and — later — `EngineApi.g.cs` (C# consumers). At runtime `@scriptFn`
 * and `@scriptEvent` do nothing; `@scriptApi` additionally stamps a small metadata record
 * on the class so the runtime {@link import('./ScriptApiRegistry').ScriptApiRegistry} can
 * read a provider's namespace / tier straight off the instance (the ergonomic
 * `registry.register(provider)` form).
 *
 * Project is configured for legacy decorators (`experimentalDecorators: true`), so the
 * markers use the legacy signatures; the tolerant optional params keep them harmless if
 * ever invoked under the TC39 (stage-3) protocol.
 *
 * Design ref: `.refactor/SCRIPTING-API-DESIGN.md` §4 (declaration) + §9 (trust tiers).
 */

/**
 * Trust tier — which class of consumer may receive a namespace (capability scoping, §9).
 * - `safe`   — ordinary user scripts (transform/sprite/physics-queries/input/audio/events).
 * - `system` — engine-system modules (raw spawn, fs, native resources, raw buffers).
 * - `editor` — editor play-mode only (gizmos, selection).
 */
export type ScriptApiTier = "safe" | "system" | "editor";

export interface ScriptApiOptions {
  /** Trust tier; defaults to `'safe'`. */
  tier?: ScriptApiTier;
  /** Per-module version (additive change = minor, breaking = major). Defaults to `1`. */
  version?: number;
}

export interface ScriptFnOptions {
  /**
   * Marks a `Promise`/`Task`-returning method. Only permitted on non-hot namespaces
   * (asset loading etc.); the codegen flags async fns so a host can enforce the policy
   * (§14 async policy).
   */
  async?: boolean;
}

/** Runtime metadata stamped on a `@scriptApi` class (read by `ScriptApiRegistry`). */
export interface ScriptApiMeta {
  readonly namespace: string;
  readonly tier: ScriptApiTier;
  readonly version: number;
}

/** Symbol carrying {@link ScriptApiMeta} on a decorated class constructor. */
export const SCRIPT_API_META = Symbol.for("engine:scriptApi:meta");

/**
 * Class marker: declares a capability module under `namespace`. Stamps {@link ScriptApiMeta}
 * so the runtime registry can read it; the codegen reads the decorator + every
 * `@scriptFn`/`@scriptEvent` member statically.
 *
 * @example
 * ```ts
 * @scriptApi('physics', { tier: 'safe', version: 1 })
 * export class PhysicsScriptApi { @scriptFn() raycast(...) { ... } }
 * ```
 */
export function scriptApi(namespace: string, options: ScriptApiOptions = {}) {
  const meta: ScriptApiMeta = {
    namespace,
    tier: options.tier ?? "safe",
    version: options.version ?? 1,
  };
  // Legacy class decorator: `target` is the constructor. We stamp metadata on it and return
  // it unchanged. (Generic over the constructor type so the return satisfies `void | typeof C`.)
  return function <TFunction extends Function>(target: TFunction): TFunction {
    try {
      Object.defineProperty(target, SCRIPT_API_META, {
        value: meta,
        enumerable: false,
        configurable: true,
      });
    } catch {
      /* frozen constructor — metadata is optional; codegen is the source of truth */
    }
    return target;
  };
}

/** Method marker: a coarse engine-API call. Read statically by the codegen; a no-op at runtime. */
export function scriptFn(_options: ScriptFnOptions = {}): MethodDecorator {
  return () => {};
}

/** Method marker: a coarse engine-API event subscription. A no-op at runtime. */
export function scriptEvent(): MethodDecorator {
  return () => {};
}

/** Read the {@link ScriptApiMeta} stamped on a `@scriptApi` class or instance, if any. */
export function getScriptApiMeta(target: unknown): ScriptApiMeta | undefined {
  if (!target) return undefined;
  const ctor = typeof target === "function" ? target : (target as object).constructor;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (ctor as any)?.[SCRIPT_API_META] as ScriptApiMeta | undefined;
}
